import type { Container, PointData, Sprite, Texture } from 'pixi.js';
import type { TileView } from './TileView';
import { TILE_Z_OFFSET } from './TileView';
import type { TileStackView } from './TileStackView';
import { setContainerParent } from '../../../../utils/pixi/transform-utils';
import { TILE_COLOR_FLIP_TEXTURES_MAP, TILE_COLOR_TEXTURE_MAP, TileColor } from '../../TileColor';
import { soundManager } from '../../../../app/SoundManager';

export interface StepConfig {
  step1: number;
  step2: number;
  step3: number;
  step4: number;
  step5: number;
  orderSwitchStep: number;
}

interface TileMoveConfig {
  arcHeight: number;
  delayBetweenTiles: number;
  tileFlipDuration: number;
  steps: {
    diagonalBottomTop: StepConfig;
    diagonalTopBottom: StepConfig;
    bottomTop: StepConfig;
    topBottom: StepConfig;
  };
}

export const tileMoveConfig: TileMoveConfig = {
  arcHeight: 100,
  delayBetweenTiles: 100,
  tileFlipDuration: 250,
  steps: {
    bottomTop: {
      step1: 0.1,
      step2: 0.25,
      step3: 0.5,
      step4: 0.75,
      step5: 0.9,
      orderSwitchStep: 2,
    },
    diagonalTopBottom: {
      step1: 0.1,
      step2: 0.25,
      step3: 0.5,
      step4: 0.75,
      step5: 0.9,
      orderSwitchStep: 1,
    },
    diagonalBottomTop: {
      step1: 0.1,
      step2: 0.25,
      step3: 0.5,
      step4: 0.75,
      step5: 0.9,
      orderSwitchStep: 1,
    },
    topBottom: {
      step1: 0.1,
      step2: 0.25,
      step3: 0.5,
      step4: 0.75,
      step5: 0.9,
      orderSwitchStep: 2,
    },
  },
};

interface FlipConfig {
  textures: string[];
  flipScaleX: number;
  defaultTexture: Texture;
  textureSteps: number[];
  zIndexFlip: number;
}

export class TileFlipAnimator {
  constructor(private readonly container: Container) {}

  public async animateTileFlip(tiles: TileView[], fromStack: TileStackView, toStack: TileStackView): Promise<void> {
    tiles.forEach((tileView, index) => {
      tileView.zIndex = fromStack.zIndex + (tiles.length - index);
      setContainerParent(tileView, this.container);
    });

    const { delayBetweenTiles, tileFlipDuration, arcHeight } = tileMoveConfig;

    const delay = delayBetweenTiles;
    const tileDuration = tileFlipDuration;

    await Promise.all(
      tiles.map(async (tileView, index) => {
        await tileView.wait(delay * index);
        await this.createTileAnimation(
          tileView,
          fromStack,
          toStack,
          tileDuration,
          toStack.zIndex + 1 + index,
          arcHeight,
        );
      }),
    );

    tiles.forEach((tileView) => {
      toStack.addTileView(tileView);
      tileView.resetView();
    });
  }

  private getFlipConfig(tileSprite: Sprite, color: TileColor, fromStack: PointData, toStack: PointData): FlipConfig {
    const flipTexturesSet = TILE_COLOR_FLIP_TEXTURES_MAP[color];

    let flipScaleX = 1;

    const fromBottom = fromStack.y > toStack.y;
    const fromLeft = fromStack.x < toStack.x;
    const diagonal = fromStack.x !== toStack.x;

    const textures: string[] = [];

    let stepConfig: StepConfig = tileMoveConfig.steps.diagonalTopBottom;

    if (diagonal) {
      textures.push(...flipTexturesSet[fromBottom ? 'diagonalTop' : 'diagonalBottom']);
      stepConfig = tileMoveConfig.steps[fromBottom ? 'diagonalBottomTop' : 'diagonalTopBottom'];
      if (fromLeft) flipScaleX = -1;
    } else {
      textures.push(...flipTexturesSet.vertical);
      stepConfig = tileMoveConfig.steps[fromBottom ? 'bottomTop' : 'topBottom'];
      if (fromBottom) textures.reverse();
    }

    const { step1, step2, step3, step4, step5, orderSwitchStep } = stepConfig;

    const steps = [step1, step2, step3, step4, step5];

    return {
      textures,
      defaultTexture: tileSprite.texture,
      flipScaleX,
      textureSteps: steps,
      zIndexFlip: steps[orderSwitchStep],
    };
  }

  private async createTileAnimation(
    tileView: TileView,
    fromStack: TileStackView,
    toStack: TileStackView,
    moveDuration: number,
    zIndexAfterFlip: number,
    pivot: number,
  ): Promise<void> {
    const { model } = tileView;
    if (!model) return;
    if (tileView.destroyed) return;
    if (toStack.destroyed) return;

    const toStackY = toStack.model.getTiles().indexOf(model) * -TILE_Z_OFFSET;
    const toPos = { x: toStack.x, y: toStack.y + toStackY };

    const { defaultTexture, textures, flipScaleX, textureSteps, zIndexFlip } = this.getFlipConfig(
      tileView.mainSprite,
      model.color,
      fromStack,
      toStack,
    );
    let currentStep = 0;

    soundManager.play(`Gameplay_Hex_Jump`);

    const textureStepPromise = tileView.tweens
      .createValueTween(0, 1, moveDuration, (value) => {
        if (tileView.destroyed) return;
        if (!textureSteps[currentStep]) return;
        if (value < textureSteps[currentStep]) return;

        tileView.scaleX = textures[currentStep] ? flipScaleX : 1;
        tileView.setTexture(textures[currentStep] ?? defaultTexture);

        currentStep += 1;

        if (value > zIndexFlip) {
          tileView.zIndex = zIndexAfterFlip;
        }
      })
      .asyncOnComplete();

    const positionTweenPromise = tileView
      .tweenTo({ x: toPos.x, y: toPos.y }, moveDuration, 'linear', true)
      .asyncOnComplete();
    await tileView.tweenTo({ pivotY: pivot }, moveDuration * 0.5, 'sinOut', true).asyncOnComplete();
    await tileView.tweenTo({ pivotY: 0 }, moveDuration * 0.5, 'sinIn', true).asyncOnComplete();
    await Promise.all([positionTweenPromise, textureStepPromise]);
  }

  public async animateFromTo(root: Container, tileSprite: Sprite, toPos: PointData): Promise<void> {
    const color =
      (Object.entries(TILE_COLOR_TEXTURE_MAP).find(
        ([_, texture]) => texture === tileSprite.texture.label,
      )?.[0] as unknown as TileColor) ?? TileColor.Blue;
    const config = this.getFlipConfig(tileSprite, color, root, toPos);

    const { defaultTexture, textures, flipScaleX, textureSteps } = config;
    let currentStep = 0;

    soundManager.play(`Gameplay_Hex_Jump`);

    const { tileFlipDuration, arcHeight } = tileMoveConfig;

    const textureStepPromise = root.tweens
      .createValueTween(0, 1, tileFlipDuration, (value) => {
        if (tileSprite.destroyed) return;
        if (!textureSteps[currentStep]) return;
        if (value < textureSteps[currentStep]) return;

        tileSprite.scaleX = textures[currentStep] ? flipScaleX : 1;
        tileSprite.props({ texture: textures[currentStep] ?? defaultTexture });

        currentStep += 1;
      })
      .asyncOnComplete();

    const positionTweenPromise = root
      .tweenTo({ x: toPos.x, y: toPos.y }, tileFlipDuration, 'linear', true)
      .asyncOnComplete();
    await root.tweenTo({ pivotY: arcHeight }, tileFlipDuration * 0.5, 'sinOut', true).asyncOnComplete();
    await root.tweenTo({ pivotY: 0 }, tileFlipDuration * 0.5, 'sinIn', true).asyncOnComplete();
    await Promise.all([positionTweenPromise, textureStepPromise]);
  }
}
